import React from "react";
import { t } from "i18next";
import {
    RefreshIcon,
    StyledIconButton,
    TrashIcon,
} from "@aureskonnect/react-ui";
import ReactSwitch from "react-switch";
import swal from "@sweetalert/with-react";
import classnames from "classnames";

import {
    setDeletedId,
    setIsDeleteModalOpened,
    setPath,
} from "../Website/CoverPage/store";
import { addFiletoStore } from "../fileStore";
import { templateActions } from "@pages/Applications/C&C/C&CSetup/templateStore";

import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { NoPicture } from "@components/Common/SvgIcons";

import { validUploadedTypes } from "../Website/constants";
import { Styles } from "../Website/CoverPage/Styles";
import UpDownArrow from "./UpDownArrow";
import { Table } from "./Table";
import LanguageTable, { AutoResizeTextarea } from "./LanguageTable";
import { templateDefaultValue } from "@constants/template";

type BannerTableType = {
    section: string;
    path: any;
    deviceType?: any;
    setDeviceType?: Function;
    sizeDesktop?: string;
    sizeMobile?: string;
    redirection?: boolean;
};
export function BannerTable({
    deviceType,
    setDeviceType,
    section,
    path,
    sizeDesktop = "500*500 (png,jpg,jpeg ou webp)",
    sizeMobile = "500*500 (png,jpg,jpeg ou webp)",
    redirection = false,
}: BannerTableType): JSX.Element {
    const uploadInputRef = React.useRef<HTMLInputElement>(null);
    let template = templateActions.getNestedObject(path);
    const currentRowIdRef = React.useRef<string | null>(null);
    const isAddingNewRef = React.useRef(false); // eslint-disable-next-line
    const data = template ? JSON.parse(JSON.stringify(template)) : [];
    const getFieldsConfig = () => {
        switch (section) {
            case "Bannier":
                return [
                    {
                        key: "Title",
                        label: "Title",
                        placeholder: "Enter the Title",
                    },
                    {
                        key: "Description",
                        label: "Description",
                        placeholder: "Enter the Description",
                    },
                ];
            case "Section2":
                return [
                    {
                        key: "Title",
                        label: "Title",
                        placeholder: "Enter the Title",
                    },
                ];
            default:
                return [];
        }
    };

    function OrderCell({ row, dragHandleProps, className }: any) {
        return (
            <div
                className="d-flex align-items-center position-relative"
                style={{ gap: "20px" }}
            >
                <UpDownArrow
                    dragHandleProps={dragHandleProps}
                    className={className}
                />

                <span
                    style={{
                        font: "normal normal 600 15px/20px Segoe UI",
                        minWidth: "20px",
                        textAlign: "center",
                    }}
                    className="epb_txt_fileOrderAS"
                >
                    {row.original.Rank}
                </span>
            </div>
        );
    }
    // eslint-disable-next-line
    function ActionsCell({ row }: any) {
        let activeScreens = templateActions
            .getNestedObject(path)
            .filter((item: any) => item.active);

        return (
            <div className="d-flex align-items-center justify-content-end position-relative">
                <ReactSwitch
                    disabled={true}
                    uncheckedIcon={false}
                    checkedIcon={false}
                    handleDiameter={26}
                    offColor="#f7b4b8"
                    offHandleColor="#E30613"
                    checked={true}
                    onChange={() => {
                        const localData = [
                            ...templateActions.getNestedObject(path),
                        ];
                        localData[row.id] = {
                            ...localData[row.id],
                            active: !row.original.active,
                        };
                        templateActions.updateTemplate(path, localData);
                    }}
                    onColor="#c2eddd"
                    onHandleColor="#34C38F"
                    width={55}
                    height={20}
                    className="epb_icn_activeDesactiveAS"
                />
                <StyledIconButton
                    icon="TrashIcon"
                    disabled={
                        activeScreens.length === 1 && row.original.active
                            ? true
                            : false
                    }
                    onClick={() => {
                        setPath(path);
                        setDeletedId(row.original.Rank);
                        setIsDeleteModalOpened(section, true);
                    }}
                    aria-label="delete"
                    className="m-0 epb_icn_archiveFileAS"
                >
                    <TrashIcon
                        style={{
                            fill: "red",
                        }}
                        height={20}
                        width={20}
                    />
                </StyledIconButton>
            </div>
        );
    }

    // eslint-disable-next-line
    function UploaderCell({ row }: any) {
        const imageTitle = row.original.Pub?.Name || "No image";

        return (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <NoPicture
                    height={35}
                    width={35}
                    onClick={() => {
                        handleUploadImageOnClickEvent(row.original.Pub.Url);
                    }}
                    className="pointer__clz"
                />

                <span
                    style={{
                        fontSize: "14px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "200px",
                    }}
                >
                    {imageTitle}
                </span>
            </div>
        );
    }

    function RedirectionCell({ row }: any) {
        const { Redirection, Rank } = row.original;
        const handleChange = React.useCallback(
            (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                try {
                    const newPath = [
                        ...path,
                        (Rank - 1).toString(),
                        "Redirection",
                    ];
                    templateActions.updateTemplate(newPath, e.target.value);
                } catch (error) {
                    console.error("Error updating redirection:", error);
                }
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            []
        );
        return (
            <AutoResizeTextarea value={Redirection} onChange={handleChange} />
        );
    }

    const columns = React.useMemo(
        () => {
            const cols = [
                {
                    Header: t("Rank"),
                    accessor: "Rank",
                    Cell: OrderCell,
                },
                {
                    Header: t("Image or video"),
                    accessor: "Pub",
                    Cell: UploaderCell,
                },
            ];

            if (redirection) {
                cols.push({
                    Header: t("Redirection"),
                    accessor: "redirection",
                    Cell: RedirectionCell, // Replace with your actual cell component
                });
            }

            cols.push({
                Header: t("Actions"),
                accessor: t("Actions"),
                Cell: ActionsCell,
            });

            return cols;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [deviceType]
    );

    async function handleUploadingNewFileRequest(file: File | undefined) {
        if (!file) return;

        const isAddingNew = isAddingNewRef.current;
        const rowIdToUpdate = currentRowIdRef.current;

        isAddingNewRef.current = false;
        currentRowIdRef.current = null;

        if (!validUploadedTypes.includes(file.type)) {
            return swal({
                icon: "error",
                content: <p>{t("File type is not valid")}!</p>,
                buttons: false,
                timer: 2000,
            });
        }

        try {
            if (isAddingNew) {
                const newBanner: any = {
                    Pub: {
                        Url: URL.createObjectURL(file),
                        Name: file.name,
                        Type: file.type,
                        Extenxion:
                            file.name.split(".").pop()?.toUpperCase() || "",
                    },
                    Rank: template ? template.length + 1 : 1,
                    Redirection: "",
                };

                const filePath: string = [...path, newBanner.Rank].join(".");
                addFiletoStore(filePath, file);

                const oldData = templateActions.getNestedObject(path) || [];
                templateActions.updateTemplate(path, [...oldData, newBanner]);
            } else if (rowIdToUpdate) {
                const updatedBanners = templateActions
                    .getNestedObject(path)
                    .map((banner: any) => {
                        if (banner.Pub.Url === rowIdToUpdate) {
                            const updatedBanner = {
                                ...banner,
                                Pub: {
                                    ...banner.Pub,
                                    Url: URL.createObjectURL(file),
                                    Name: file.name,
                                    Type: file.type,
                                    Extenxion:
                                        file.name
                                            .split(".")
                                            .pop()
                                            ?.toUpperCase() || "",
                                },
                            };
                            const filePath: string = [
                                ...path,
                                banner.Rank,
                            ].join(".");
                            addFiletoStore(filePath, file);
                            return updatedBanner;
                        }
                        return banner;
                    });

                templateActions.updateTemplate(path, updatedBanners);
            }

            swal({
                icon: "success",
                content: <p>{t("File Uploaded successfully")}!</p>,
                buttons: false,
                timer: 2000,
            });
        } catch (error: any) {
            swal({
                icon: "error",
                content: <p>{t(error.message)}</p>,
                buttons: false,
                timer: 2000,
            });
        } finally {
            if (uploadInputRef.current) {
                uploadInputRef.current.value = "";
            }
        }
    }

    function handleUploadImageOnClickEvent(rowId: string) {
        isAddingNewRef.current = false;
        currentRowIdRef.current = rowId;
        uploadInputRef.current?.click();
    }
    function handleUploadedDataOnChangeEvent(
        event: React.ChangeEvent<HTMLInputElement>
    ) {
        const file = event.target.files?.[0];
        if (file) {
            handleUploadingNewFileRequest(file).finally(() => {
                if (uploadInputRef.current) uploadInputRef.current.value = "";
            });
        }
    }

    async function handleResetOnClickEvent(): Promise<void> {
        templateActions.updateTemplate(path, []);
        if (uploadInputRef.current) {
            uploadInputRef.current.value = "";
        }
    }

    return (
        <div
            className="d-flex flex-column cursor__clz "
            style={{
                gap: "10px",
                flexGrow: 1,
                minHeight: "300px",
            }}
        >
            {!path.includes("Application") ? (
                <span
                    style={{
                        top: "630px",
                        left: "321px",
                        width: "153px",
                        height: "21px",
                        textAlign: "left",
                        font: "normal normal 600 16px/21px Segoe UI",
                        letterSpacing: "0px",
                        color: "#000000",
                        opacity: "1",
                        marginLeft: "3%",
                        marginTop: "1%",
                    }}
                >
                    {section === "Bannier" ? t("Banner image") : t("Images")}
                </span>
            ) : null}

            <div className="d-flex justify-content-between align-items-center">
                {!path.includes("Application") && setDeviceType ? (
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <select
                            value={deviceType}
                            onChange={(e: any) =>
                                setDeviceType(
                                    e.target.value as "Desktop" | "Mobile"
                                )
                            }
                            className="form-select ml-4 py-1 px-2 rounded-sm text-xl"
                            style={{ width: "200px", background: "center" }}
                        >
                            <option value="Desktop">Desktop</option>
                            <option value="Mobile">Mobile</option>
                        </select>
                        <span
                            className="py-2 px-2"
                            style={{ color: "#666", fontSize: "14px" }}
                        >
                            {deviceType === "Desktop"
                                ? sizeDesktop
                                : sizeMobile}
                        </span>
                    </div>
                ) : (
                    <div></div>
                )}
                <div
                    className="d-flex align-items-center"
                    style={{
                        gap: "20px",
                    }}
                >
                    <StyledIconButton
                        title={t("Reset to default settings")}
                        outline={true}
                        variant="danger"
                        className="m-0 p-0 epb_icn_resetModificationsAS"
                        icon="RefreshIcon"
                        onClick={handleResetOnClickEvent}
                    >
                        <RefreshIcon height={20} width={20} />
                    </StyledIconButton>

                    <div className="d-flex">
                        <input
                            accept="video/*,image/*"
                            multiple={false}
                            type="file"
                            id="fileUpload"
                            hidden={true}
                            ref={uploadInputRef}
                            onChange={handleUploadedDataOnChangeEvent}
                            onClick={(event: any) => {
                                event.currentTarget.value = "";
                            }}
                        />
                        <CustomMainColorButton
                            rounded={true}
                            variant="primary"
                            onClick={() => {
                                isAddingNewRef.current = true;
                                uploadInputRef.current?.click();
                            }}
                            className={`${classnames(
                                "w-20 cmn_btn_SaveButton mr-5",
                                {
                                    "not-allowed-icon__clz":
                                        section === "Section1" &&
                                        template !== null &&
                                        template.length === 3,
                                }
                            )}`}
                        >
                            {t("Add")}
                        </CustomMainColorButton>
                    </div>
                </div>
            </div>
            <div className="d-flex flex-column">
                <Styles>
                    <Table
                        columns={columns}
                        data={data}
                        deviceType={deviceType}
                        section={section}
                        path={path}
                    />
                </Styles>
                {["Bannier", "Section2"].includes(section) && (
                    <LanguageTable
                        path={["SiteWeb", "LaindingPage", section]}
                        fields={getFieldsConfig()}
                        defaultTexts={{
                            Title:
                                section === "Section2"
                                    ? templateDefaultValue.SiteWeb.LaindingPage
                                          .Section2.Title
                                    : templateDefaultValue.SiteWeb.LaindingPage
                                          .Bannier.Title,
                            Description:
                                templateDefaultValue.SiteWeb.LaindingPage
                                    .Bannier.Description,
                        }}
                        sectionTitle={
                            section === "Bannier"
                                ? "Title and Description"
                                : "Title"
                        }
                    />
                )}
            </div>
        </div>
    );
}
